import React from 'react';
import { MapPin, Phone, Clock, Car } from 'lucide-react';
import { CompanyAddress, CompanyPhone } from '../../../models';

const IntroAcademy: React.FC = () => {
    return (
        <div className="animate-in fade-in duration-700">
            {/* Hero Section */}
            <section className="py-16 md:py-20 px-4 md:px-8 bg-gradient-to-br from-slate-900 to-indigo-950 text-white relative overflow-hidden">
                <div className="max-w-5xl mx-auto text-center space-y-6 relative z-10">
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 text-indigo-300 font-black text-[10px] uppercase tracking-wider border border-white/10">
                        About Us
                    </div>
                    <h1 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-black leading-tight">
                        학생과 학부모가 신뢰하는 <br />
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-violet-400 leading-tight">정성우학원의 교육 철학</span>
                    </h1>
                    <p className="text-sm sm:text-base md:text-lg lg:text-xl text-slate-400 font-medium max-w-2xl mx-auto">
                        더 큰 성장을 이끌어가는 정성우학원의 <br />
                        원장 소개와 교육 비전을 전해드립니다.
                    </p>
                </div>
                <div className="absolute top-0 right-0 w-96 h-96 bg-indigo-600/20 blur-[100px] rounded-full"></div>
            </section>

            {/* Content Section */}
            <section className="py-16 md:py-24 px-4 md:px-8 max-w-5xl mx-auto">
                <div className="space-y-20 md:space-y-24">
                    {/* Director's Greeting */}
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
                        {/* Director's Image */}
                        <div className="md:col-span-5 flex justify-center">
                            <div className="relative group w-full max-w-[320px] aspect-[4/5] rounded-[3rem] overflow-hidden border-2 border-indigo-500/20 shadow-2xl shadow-indigo-950/50">
                                <img
                                    src="/director_v3.jpg"
                                    alt="정성우 원장"
                                    loading="lazy"
                                    decoding="async"
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-transparent to-transparent opacity-85"></div>
                                <div className="absolute bottom-6 left-6 text-white">
                                    <p className="text-xs font-black uppercase tracking-widest text-indigo-400">Director</p>
                                    <h4 className="text-lg font-black mt-1">정성우 원장</h4>
                                </div>
                            </div>
                        </div>

                        {/* Director's Message */}
                        <div className="md:col-span-7 space-y-6 text-left">
                            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-500/10 text-indigo-300 font-black text-[10px] uppercase tracking-wider border border-indigo-500/20">
                                Director's Message
                            </div>
                            <h3 className="text-xl sm:text-2xl md:text-3xl font-black text-white leading-tight tracking-tight">
                                "배움을 넘어 성장을 향해 나아갑니다"
                            </h3>
                            <div className="space-y-4 text-slate-300 font-medium leading-relaxed text-sm sm:text-base break-keep">
                                <p>
                                    안녕하십니까, 정성우학원 원장 정성우입니다.
                                </p>
                                <p>
                                    교육은 단순히 지식을 일방적으로 전달하는 것을 넘어, 학생 스스로가 배움의 주체가 되어 성장하는 과정이어야 합니다. 
                                    저희 학원은 개별 학생의 눈높이에 맞춘 <strong>정밀한 맞춤 관리</strong>와 따뜻한 동반자로서의 지도를 지향합니다.
                                </p>
                                <p>
                                    최신 인공지능 학습 분석 기술과 오랫동안 쌓아온 밀착 피드백 노하우를 융합하여, 
                                    학습에 어려움을 겪는 학생부터 최상위권 학생까지 각자의 목표를 확실히 달성할 수 있도록 끝까지 신뢰를 바탕으로 책임 지도하겠습니다.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Academy Vision */}
                    <div className="space-y-12 pt-12 border-t border-indigo-950/40">
                        <div className="text-center space-y-4">
                            <span className="text-xs font-black uppercase tracking-widest text-indigo-400">Our Vision</span>
                            <h3 className="text-xl sm:text-2xl md:text-3xl font-black text-white tracking-tight break-keep">
                                정성우학원이 나아가는 <span className="text-indigo-400">세 가지 비전</span>
                            </h3>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-left">
                            <div className="p-6 md:p-8 rounded-[2.5rem] bg-[#111A36]/30 border border-indigo-950/40 space-y-4 hover:border-indigo-500/20 transition-all duration-300 shadow-sm">
                                <div className="w-12 h-12 rounded-2xl bg-indigo-500/10 flex items-center justify-center text-indigo-400 font-black text-lg">
                                    01
                                </div>
                                <h4 className="text-base sm:text-lg font-black text-slate-200">진정한 자기주도학습</h4>
                                <p className="text-slate-450 text-xs sm:text-sm font-medium leading-relaxed break-keep">
                                    무조건적인 주입식 교육에서 벗어나, 학생 스스로 스케줄을 이해하고 실행에 옮길 수 있는 주체적인 학습력을 길러냅니다.
                                </p>
                            </div>
                            <div className="p-6 md:p-8 rounded-[2.5rem] bg-[#111A36]/30 border border-indigo-950/40 space-y-4 hover:border-indigo-500/20 transition-all duration-300 shadow-sm">
                                <div className="w-12 h-12 rounded-2xl bg-indigo-500/10 flex items-center justify-center text-indigo-400 font-black text-lg">
                                    02
                                </div>
                                <h4 className="text-base sm:text-lg font-black text-slate-200">데이터 기반 정밀 케어</h4>
                                <p className="text-slate-450 text-xs sm:text-sm font-medium leading-relaxed break-keep">
                                    AI 모니터링 및 실시간 학습 이행률 데이터를 토대로 각 단원의 부족함이나 스케줄의 병목을 과학적으로 정확하게 보완합니다.
                                </p>
                            </div>
                            <div className="p-6 md:p-8 rounded-[2.5rem] bg-[#111A36]/30 border border-indigo-950/40 space-y-4 hover:border-indigo-500/20 transition-all duration-300 shadow-sm">
                                <div className="w-12 h-12 rounded-2xl bg-indigo-500/10 flex items-center justify-center text-indigo-400 font-black text-lg">
                                    03
                                </div>
                                <h4 className="text-base sm:text-lg font-black text-slate-200">투명한 소통과 높은 신뢰</h4>
                                <p className="text-slate-450 text-xs sm:text-sm font-medium leading-relaxed break-keep">
                                    학습 진도, 스케줄 이행 결과 등의 정보를 학부모님께 실시간 공유함으로써 안심하고 믿고 맡길 수 있는 교육을 실천합니다.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Location & Contact Section */}
                    <div className="mt-20 pt-12 border-t border-indigo-950/40 space-y-12">
                        <div className="text-center space-y-4">
                            <span className="text-xs font-black uppercase tracking-widest text-indigo-400">Directions</span>
                            <h3 className="text-xl sm:text-2xl md:text-3xl font-black text-white tracking-tight break-keep">
                                정성우학원 <span className="text-indigo-400">오시는 길</span>
                            </h3>
                        </div>
                        <div className="bg-[#111A36]/30 border border-indigo-950/40 rounded-[2.5rem] overflow-hidden shadow-2xl flex flex-col md:flex-row hover:border-indigo-500/20 transition-all duration-300 w-full md:h-[520px]">
                            {/* Left: Entrance Image */}
                            <div className="md:w-1/2 relative h-[300px] md:h-full">
                                <img
                                    src="/academy_entrance.png"
                                    alt="정성우학원 입구"
                                    loading="lazy"
                                    decoding="async"
                                    className="w-full h-full object-cover"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t md:bg-gradient-to-r from-transparent to-[#0B132B]/30"></div>
                            </div>

                            {/* Right: Info Fields */}
                            <div className="md:w-1/2 flex flex-col justify-between divide-y divide-indigo-950/30 bg-transparent h-auto md:h-full py-2">
                                {/* Address */}
                                <div className="py-3.5 px-6 flex items-start gap-4 text-left">
                                    <div className="w-9 h-9 rounded-2xl bg-indigo-500/10 flex items-center justify-center shrink-0 border border-indigo-500/20">
                                        <MapPin className="text-indigo-400" size={16} />
                                    </div>
                                    <div className="space-y-0.5">
                                        <h4 className="text-[9px] font-black text-indigo-400 uppercase tracking-widest pl-1">주소</h4>
                                        <p className="text-xs sm:text-sm font-bold text-slate-200 leading-normal pl-1">
                                            {CompanyAddress}
                                        </p>
                                    </div>
                                </div>

                                {/* Phone */}
                                <div className="py-3.5 px-6 flex items-start gap-4 text-left">
                                    <div className="w-9 h-9 rounded-2xl bg-indigo-500/10 flex items-center justify-center shrink-0 border border-indigo-500/20">
                                        <Phone className="text-indigo-400" size={16} />
                                    </div>
                                    <div className="space-y-0.5">
                                        <h4 className="text-[9px] font-black text-indigo-400 uppercase tracking-widest pl-1">전화</h4>
                                        <p className="text-sm sm:text-base font-black text-indigo-300 pl-1">
                                            {CompanyPhone}
                                        </p>
                                    </div>
                                </div>

                                {/* Hours */}
                                <div className="py-3.5 px-6 flex items-start gap-4 text-left">
                                    <div className="w-9 h-9 rounded-2xl bg-indigo-500/10 flex items-center justify-center shrink-0 border border-indigo-500/20">
                                        <Clock className="text-indigo-400" size={16} />
                                    </div>
                                    <div className="space-y-0.5">
                                        <h4 className="text-[9px] font-black text-indigo-400 uppercase tracking-widest pl-1">운영시간</h4>
                                        <p className="text-xs sm:text-sm font-bold text-slate-200 pl-1 leading-normal">
                                            월 - 금: 13:00 - 22:00 <span className="text-slate-450 ml-2 font-medium">| 토, 일·공휴일: 휴원</span>
                                        </p>
                                    </div>
                                </div>

                                {/* Parking */}
                                <div className="py-3.5 px-6 flex items-start gap-4 text-left">
                                    <div className="w-9 h-9 rounded-2xl bg-indigo-500/10 flex items-center justify-center shrink-0 border border-indigo-500/20">
                                        <Car className="text-indigo-400" size={16} />
                                    </div>
                                    <div className="space-y-0.5">
                                        <h4 className="text-[9px] font-black text-indigo-400 uppercase tracking-widest pl-1">주차 안내</h4>
                                        <p className="text-xs sm:text-sm font-bold text-slate-200 pl-1">
                                            건물 내 주차 가능 (무료 주차 등록 지원)
                                        </p>
                                    </div>
                                </div>

                                {/* Map Button */}
                                <div className="p-3 px-6 bg-[#111A36]/10">
                                    <a
                                        href="https://map.naver.com/v5/search/%EC%A0%95%EC%84%B1%EC%9A%B0%ED%95%99%EC%9B%90"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="w-full bg-indigo-600 hover:bg-indigo-500 text-white py-2.5 rounded-xl font-black text-sm block text-center transition-all duration-300 shadow-lg shadow-indigo-900/20 active:scale-98"
                                    >
                                        네이버 지도로 길찾기
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default IntroAcademy;
